import { LocalDate } from "@js-joda/core";
import { PayoutType, TipStatus } from "model/Tip";
import type {
  CalculateTipsResponse,
  SaveTipPeriodRequest,
  TipDetails,
  WorkGroup,
  WorkGroupPayload,
} from "model/Tip";
import {
  calculateTipsDistribution,
  cancelTips,
  createTipDate,
  deleteTip,
  fetchTipById,
  fetchTipsByDateRange,
  fetchTipsByMonth,
  payoutTips,
  saveTipsDistribution,
  updateTipAmount,
} from "services/api/company/tips.v2.service";
import {
  getBackendErrorMessageFromUnknown,
  getBackendErrorPayloadFromUnknown,
} from "services/api/backendError";
import { err, ok, type Result } from "utils/result";

export type TipsV2Error =
  | "add-failed"
  | "calculate-failed"
  | "cancel-payout-failed"
  | "delete-failed"
  | "load-failed"
  | "payout-failed"
  | "payout-missing-swish-number"
  | "save-distribution-failed"
  | "update-failed";

export interface AddUniqueTipDatesResult {
  readonly skippedDates: readonly string[];
  readonly tips: readonly TipDetails[];
}

export interface TipsSummaryModel {
  readonly allTipsAmount: number;
  readonly paidOutAmount: number;
  readonly readyToPayoutAmount: number;
}

export type TipsSummaryFilter = "all" | "paidOut" | "readyToPayout";

export interface TipDistributionDraft {
  readonly companyTipId: number;
  readonly remainingAmount: number;
  readonly workers: readonly SaveTipPeriodRequest["workers"][number][];
}

interface AddUniqueTipDatesParams {
  readonly companyId: number;
  readonly selectedDates: readonly LocalDate[];
}

interface AddTipDatesByKeyParams {
  readonly companyId: number;
  readonly dateKeys: readonly string[];
}

interface CalculateTipDistributionParams {
  readonly companyId: number;
  readonly companyTipId: number | readonly number[];
  readonly workGroups?: readonly WorkGroupPayload[];
}

interface CancelManualPayoutParams {
  readonly companyId: number;
  readonly companyTipIds: readonly number[];
}

interface FilterTipsBySummaryFilterParams {
  readonly filter: TipsSummaryFilter;
  readonly tips: readonly TipDetails[];
}

interface LoadTipsForMonthParams {
  readonly companyId: number;
  readonly monthDate: LocalDate;
}

interface MergeTipsParams {
  readonly currentTips: readonly TipDetails[];
  readonly nextTips: readonly TipDetails[];
}

interface PayoutTipParams {
  readonly companyId: number;
  readonly companyTipId: number;
  readonly payoutType: PayoutType;
}

interface PayoutTipBatchParams {
  readonly companyId: number;
  readonly companyTipIds: readonly number[];
  readonly payoutType: PayoutType;
}

export interface TipPayoutError {
  readonly backendMessage: string | null;
  readonly type: "payout-failed" | "payout-missing-swish-number";
}

interface RemoveTipParams {
  readonly companyId: number;
  readonly companyTipId: number;
}

interface RemoveTipsParams {
  readonly companyId: number;
  readonly companyTipIds: readonly number[];
}

interface SaveTipAmountParams {
  readonly amount: number;
  readonly companyId: number;
  readonly companyTipId: number;
}

interface SaveTipDistributionParams {
  readonly companyId: number;
  readonly drafts: readonly TipDistributionDraft[];
}

function pad(value: number): string {
  return value.toString().padStart(2, "0");
}

const MISSING_SWISH_NUMBER_ERROR_CODE = 2;

function getBackendErrorCode(error: unknown): number | null {
  return getBackendErrorPayloadFromUnknown(error)?.errorCode ?? null;
}

function getTypedBackendErrorMessage(error: unknown): string | null {
  return getBackendErrorMessageFromUnknown(error);
}

function toPayoutError(error: unknown): TipPayoutError {
  const backendMessage = getTypedBackendErrorMessage(error);

  if (getBackendErrorCode(error) === MISSING_SWISH_NUMBER_ERROR_CODE) {
    return {
      backendMessage,
      type: "payout-missing-swish-number",
    };
  }

  return {
    backendMessage,
    type: "payout-failed",
  };
}

/** Returns the API date key for a tip date. */
export function toTipDateKey(date: LocalDate): string {
  return `${date.year()}-${pad(date.monthValue())}-${pad(date.dayOfMonth())}`;
}

/** Returns the API month key for loading tips. */
export function toTipMonthKey(date: LocalDate): string {
  return `${date.year()}-${pad(date.monthValue())}`;
}

function hasDistributedAmounts(tip: TipDetails): boolean {
  return (
    tip.remainingAmount < tip.amount ||
    tip.workers.some((worker) => worker.tipAmount > 0)
  );
}

/** Checks whether a tip is ready for payout. */
export function isReadyToPayoutTip(tip: TipDetails): boolean {
  return tip.status === TipStatus.Done && hasDistributedAmounts(tip);
}

/** Checks whether a tip has any approved time reports available for distribution. */
export function hasApprovedTimeReports(tip: TipDetails): boolean {
  if (tip.approvedCount > 0) {
    return true;
  }

  return tip.workers.some((worker) => worker.approvedTimeReportCount > 0);
}

/** Checks whether a tip already has a persisted distribution that can be edited. */
export function hasPersistedDistribution(tip: TipDetails): boolean {
  return hasDistributedAmounts(tip);
}

/** Checks whether a tip has been paid out. */
export function isPaidOutTip(tip: TipDetails): boolean {
  return (
    tip.status === TipStatus.Closed || tip.status === TipStatus.ClosedManually
  );
}

/** Filters tips for the active summary filter. */
export function filterTipsBySummaryFilter({
  filter,
  tips,
}: FilterTipsBySummaryFilterParams): TipDetails[] {
  switch (filter) {
    case "all":
      return [...tips];
    case "readyToPayout":
      return tips.filter(isReadyToPayoutTip);
    case "paidOut":
      return tips.filter(isPaidOutTip);
  }
}

/** Sorts tips by date in ascending order. */
export function sortTipsByDate(tips: readonly TipDetails[]): TipDetails[] {
  return [...tips].sort((a, b) => a.date.localeCompare(b.date));
}

/** Merges incoming tips into the current tip list by id. */
export function mergeTips({
  currentTips,
  nextTips,
}: MergeTipsParams): TipDetails[] {
  const tipsById = new Map<number, TipDetails>();
  currentTips.forEach((tip) => tipsById.set(tip.companyTipId, tip));
  nextTips.forEach((tip) => tipsById.set(tip.companyTipId, tip));
  return sortTipsByDate(Array.from(tipsById.values()));
}

/** Builds a calculation result shape from already persisted tip distributions. */
export function toCalculationResultFromTips(
  tips: readonly TipDetails[],
): CalculateTipsResponse {
  const workGroupsById = new Map<number, WorkGroup>();

  tips.forEach((tip) => {
    tip.workGroups.forEach((group) => {
      workGroupsById.set(group.workGroupId, group);
    });

    tip.calculationSettings?.workGroups?.forEach((group) => {
      workGroupsById.set(group.workGroupId, group);
    });
  });

  return {
    companyTips: tips.map((tip) => ({
      ...tip,
      workers: tip.workers.map((worker) => ({ ...worker })),
      workGroups: tip.workGroups.map((group) => ({ ...group })),
    })),
    workGroups: Array.from(workGroupsById.values()).map((group) => ({
      ...group,
    })),
  };
}

/** Loads all tips for a company month. */
export async function loadTipsForMonth({
  companyId,
  monthDate,
}: LoadTipsForMonthParams): Promise<Result<TipDetails[], TipsV2Error>> {
  try {
    const response = await fetchTipsByMonth(
      companyId,
      toTipMonthKey(monthDate),
    );
    return ok(sortTipsByDate(response.data));
  } catch (_error: unknown) {
    return err("load-failed");
  }
}

/** Creates missing tip dates and reports dates that already exist. */
export async function addUniqueTipDates({
  companyId,
  selectedDates,
}: AddUniqueTipDatesParams): Promise<
  Result<AddUniqueTipDatesResult, TipsV2Error>
> {
  const dateKeys = Array.from(new Set(selectedDates.map(toTipDateKey))).sort();
  if (dateKeys.length === 0) return ok({ skippedDates: [], tips: [] });

  try {
    const firstDate = dateKeys[0];
    const lastDate = dateKeys[dateKeys.length - 1];
    const existingResponse = await fetchTipsByDateRange(
      companyId,
      firstDate,
      lastDate,
    );
    const existingDateKeys = new Set(
      existingResponse.data.companyTips.map((tip) => tip.date.slice(0, 10)),
    );
    const uniqueDateKeys = dateKeys.filter(
      (date) => !existingDateKeys.has(date),
    );
    const skippedDates = dateKeys.filter((date) => existingDateKeys.has(date));

    const createdTips = await Promise.all(
      uniqueDateKeys.map(async (date) => {
        await createTipDate(companyId, date);
        const createdResponse = await fetchTipsByDateRange(
          companyId,
          date,
          date,
        );
        return createdResponse.data.companyTips;
      }),
    );

    return ok({
      skippedDates,
      tips: sortTipsByDate(createdTips.flat()),
    });
  } catch (_error: unknown) {
    return err("add-failed");
  }
}

/** Creates tip dates for the provided date keys, including dates that already exist. */
export async function addTipDatesByKey({
  companyId,
  dateKeys,
}: AddTipDatesByKeyParams): Promise<
  Result<readonly TipDetails[], TipsV2Error>
> {
  const uniqueDateKeys = Array.from(new Set(dateKeys)).sort();
  if (uniqueDateKeys.length === 0) return ok([]);

  try {
    const createdTips = await Promise.all(
      uniqueDateKeys.map(async (date) => {
        await createTipDate(companyId, date);
        const createdResponse = await fetchTipsByDateRange(
          companyId,
          date,
          date,
        );
        return createdResponse.data.companyTips;
      }),
    );

    return ok(sortTipsByDate(createdTips.flat()));
  } catch (_error: unknown) {
    return err("add-failed");
  }
}

/** Saves a tip amount and returns the refreshed tip. */
export async function saveTipAmount({
  amount,
  companyId,
  companyTipId,
}: SaveTipAmountParams): Promise<Result<TipDetails, TipsV2Error>> {
  try {
    await updateTipAmount(companyId, companyTipId, amount);
    const response = await fetchTipById(companyId, companyTipId);
    return ok(response.data);
  } catch (_error: unknown) {
    return err("update-failed");
  }
}

/** Removes a single tip. */
export async function removeTip({
  companyId,
  companyTipId,
}: RemoveTipParams): Promise<Result<void, TipsV2Error>> {
  try {
    await deleteTip(companyId, companyTipId);
    return ok(undefined);
  } catch (_error: unknown) {
    return err("delete-failed");
  }
}

/** Removes several tips. */
export async function removeTips({
  companyId,
  companyTipIds,
}: RemoveTipsParams): Promise<Result<void, TipsV2Error>> {
  try {
    await Promise.all(
      companyTipIds.map((companyTipId) => deleteTip(companyId, companyTipId)),
    );
    return ok(undefined);
  } catch (_error: unknown) {
    return err("delete-failed");
  }
}

/** Calculates tip distribution for one or more tips. */
export async function calculateTipDistribution({
  companyId,
  companyTipId,
  workGroups = [],
}: CalculateTipDistributionParams): Promise<
  Result<CalculateTipsResponse, TipsV2Error>
> {
  try {
    const companyTipIds = Array.isArray(companyTipId)
      ? companyTipId
      : [companyTipId];
    const response = await calculateTipsDistribution(companyId, {
      companyTipIds: [...companyTipIds],
      workGroups: workGroups.map((group) => ({
        id: group.id,
        percentage: group.percentage,
      })),
    });

    return ok(response.data);
  } catch (_error: unknown) {
    return err("calculate-failed");
  }
}

/** Saves calculated tip distribution drafts. */
export async function saveTipDistribution({
  companyId,
  drafts,
}: SaveTipDistributionParams): Promise<Result<TipDetails[], TipsV2Error>> {
  try {
    const payload: SaveTipPeriodRequest[] = drafts
      .filter((draft) => draft.workers.length > 0)
      .map((draft) => ({
        companyTipId: draft.companyTipId,
        remainingAmount: draft.remainingAmount,
        workers: draft.workers.map((worker) => ({
          workerId: worker.workerId,
          workTime: worker.workTime,
          tipAmount: worker.tipAmount,
          workGroupId: worker.workGroupId,
        })),
      }));

    const response = await saveTipsDistribution(companyId, payload);
    return ok(response.data.companyTips);
  } catch (_error: unknown) {
    return err("save-distribution-failed");
  }
}

/** Pays out a single tip and returns the refreshed tip. */
export async function payoutTip({
  companyId,
  companyTipId,
  payoutType,
}: PayoutTipParams): Promise<Result<TipDetails, TipPayoutError>> {
  const result = await payoutTipBatch({
    companyId,
    companyTipIds: [companyTipId],
    payoutType,
  });
  if (!result.ok) return result;

  const tip = result.value[0];
  return tip
    ? ok(tip)
    : err({
        backendMessage: null,
        type: "payout-failed",
      });
}

/** Pays out several tips and returns their refreshed details. */
export async function payoutTipBatch({
  companyId,
  companyTipIds,
  payoutType,
}: PayoutTipBatchParams): Promise<Result<TipDetails[], TipPayoutError>> {
  try {
    await payoutTips(companyId, [...companyTipIds], payoutType);
    const responses = await Promise.all(
      companyTipIds.map((companyTipId) =>
        fetchTipById(companyId, companyTipId),
      ),
    );
    return ok(responses.map((response) => response.data));
  } catch (error: unknown) {
    return err(toPayoutError(error));
  }
}

/** Cancels manual payout for several tips. */
export async function cancelManualPayout({
  companyId,
  companyTipIds,
}: CancelManualPayoutParams): Promise<Result<TipDetails[], TipsV2Error>> {
  try {
    const response = await cancelTips(
      companyId,
      [...companyTipIds],
      PayoutType.Manual,
    );
    return ok(response.data);
  } catch (_error: unknown) {
    return err("cancel-payout-failed");
  }
}

/** Builds saveable distribution drafts from calculated tips. */
export function toTipDistributionDrafts(
  companyTips: readonly TipDetails[],
): TipDistributionDraft[] {
  return companyTips.map((tip) => ({
    companyTipId: tip.companyTipId,
    remainingAmount: tip.remainingAmount,
    workers: tip.workers.map((worker) => ({
      workerId: worker.workerId,
      workTime: worker.workTime,
      tipAmount: worker.tipAmount,
      workGroupId: worker.workGroupId,
    })),
  }));
}

/** Builds the summary totals for the tips list. */
export function buildTipsSummary(
  tips: readonly TipDetails[],
): TipsSummaryModel {
  return tips.reduce<TipsSummaryModel>(
    (summary, tip) => ({
      allTipsAmount: summary.allTipsAmount + tip.amount,
      paidOutAmount: isPaidOutTip(tip)
        ? summary.paidOutAmount + tip.amount
        : summary.paidOutAmount,
      readyToPayoutAmount: isReadyToPayoutTip(tip)
        ? summary.readyToPayoutAmount + tip.amount
        : summary.readyToPayoutAmount,
    }),
    {
      allTipsAmount: 0,
      paidOutAmount: 0,
      readyToPayoutAmount: 0,
    },
  );
}
